import { useTranslation } from 'react-i18next';
import { CircleAlert, IdCard, UserRound } from 'lucide-react';
import { formatIqd, fromMillidinars } from '@gc/shared';

import type { TapOutcome } from './cards';

interface TapResultCardProps {
  outcome: TapOutcome;
  currencySymbol: string;
  onOpenProfile: (customerId: string) => void;
  /** Hand the unrecognised UID to the assign flow, prefilled. */
  onAssign: (cardId: string) => void;
  onDismiss: () => void;
}

/**
 * What a tap resolved to. Presentational on purpose — the matched/unmatched
 * branches are asserted by static-markup tests, so no effects and no fetches.
 */
export function TapResultCard({
  outcome,
  currencySymbol,
  onOpenProfile,
  onAssign,
  onDismiss,
}: TapResultCardProps) {
  const { t } = useTranslation();

  if (outcome.kind === 'unmatched') {
    return (
      <div data-outcome="unmatched" className="rounded-card border border-danger/40 bg-raised p-4">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-content">
              {t('cards.result.unmatchedTitle')}
            </h3>
            <p className="mt-1 text-sm text-content-secondary">
              {t('cards.result.unmatchedBody', { cardId: outcome.cardId })}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('cards.result.dismiss')}
          </button>
          <button
            type="button"
            onClick={() => onAssign(outcome.cardId)}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
          >
            {t('cards.result.assign')}
          </button>
        </div>
      </div>
    );
  }

  const { account } = outcome;
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  return (
    <div data-outcome="matched" className="rounded-card border border-subtle bg-raised p-4">
      <div className="flex items-start gap-3">
        <UserRound className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-content">{account.fullName}</h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-content-muted">
            <IdCard className="h-3.5 w-3.5" aria-hidden />
            <span className="tabular">{account.cardId ?? '—'}</span>
          </p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-card bg-subtle">
        <div className="bg-ground px-3 py-2">
          <dt className="text-xs text-content-secondary">{t('cards.result.balance')}</dt>
          <dd className="tabular text-lg font-semibold text-content">{money(account.balanceMd)}</dd>
        </div>
        <div className="bg-ground px-3 py-2">
          <dt className="text-xs text-content-secondary">{t('cards.result.points')}</dt>
          <dd className="tabular text-lg font-semibold text-content">{account.points}</dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
        >
          {t('cards.result.dismiss')}
        </button>
        <button
          type="button"
          onClick={() => onOpenProfile(account.id)}
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          {t('cards.result.openProfile')}
        </button>
      </div>
    </div>
  );
}
